import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'
import { useGetCouplePlanningList } from 'src/hooks/api/couple-planning'
import { useGetFarmList } from 'src/hooks/api/farm'
import { useGetLotList } from 'src/hooks/api/lot'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { CoverageProcessParams } from 'src/domain/coverage-process'
import { useCreateCouple, useCreateMultipleCouples } from 'src/hooks/api/pregnancy-diagnosis'
import { useGetAnimalListByLot } from 'src/hooks/api/animal'
import { filterAnimalsByReproductionAge } from '../../../utils/functions'

export function useRegisterCouple() {
  const [showAnimalsModal, setShowAnimalsModal] = useState<boolean>(false)
  const { id: registerCoupleId } = useParams()
  const { handleSubmit, reset, watch: watchForm } = useFormContext<CoverageProcessParams>()

  const { data: couplePlanningList, isLoading: isLoadingCouplePlanning } = useGetCouplePlanningList()
  const { data: propertyList, isLoading: isLoadingPropertyList } = useGetFarmList()
  const { data: lotList, isLoading: isLoadingLotList } = useGetLotList()

  const { mutateAsync: createCoupleAsync, isPending: isLoadingCreateCouple } = useCreateCouple()
  const { mutateAsync: createMultipleCouplesAsync, isPending: isLoadingCreateMultipleCouples } =
    useCreateMultipleCouples()

  const selectedLotId = watchForm('idLote')
  const { data: animalListByLot, isLoading: isLoadingAnimalsByLot } = useGetAnimalListByLot(selectedLotId || '', {
    sexo: 'Fêmea'
  })

  const selectedMontaType = watchForm('tipoMonta')

  const registerCoupleData = useMemo(() => {
    return couplePlanningList?.find((item) => item.id === registerCoupleId)
  }, [couplePlanningList, registerCoupleId])

  const validAnimalsFromLot = useMemo(() => {
    if (!animalListByLot || selectedMontaType !== 'monta') return []
    const activeAnimals = animalListByLot.filter((animal) => animal.categoria !== 'VD' && animal.categoria !== 'MT')
    return filterAnimalsByReproductionAge(activeAnimals)
  }, [animalListByLot, selectedMontaType])

  const lotValidationError = useMemo(() => {
    if (!selectedLotId || selectedMontaType !== 'monta') return null
    if (isLoadingAnimalsByLot) return null
    if (validAnimalsFromLot.length === 0) {
      return 'Nenhum animal fêmea apto para reprodução (maior que 12 meses) foi encontrado neste lote.'
    }
    return null
  }, [selectedLotId, selectedMontaType, isLoadingAnimalsByLot, validAnimalsFromLot])

  const onSubmit = handleSubmit(async (formData: CoverageProcessParams) => {
    try {
      if (formData.tipoMonta === 'monta' && formData.idLote && validAnimalsFromLot.length > 0) {
        await createMultipleCouplesAsync({
          animals: validAnimalsFromLot,
          commonData: {
            idTouro: formData.idTouro || '',
            dataInicio: formData.dataInicio ? new Date(formData.dataInicio).toISOString() : new Date().toISOString(),
            dataFim: formData.dataFim ? new Date(formData.dataFim).toISOString() : null,
            tipoMonta: formData.tipoMonta as any,
            inseminador: formData.inseminador || undefined,
            dosagem: formData.dosagem || undefined
          }
        })
      } else {
        const diagnosisData = {
          idFazenda: formData.idFazenda,
          idAnimal: formData.idVaca,
          touro: formData.idTouro || '',
          tipoReproducao: formData.tipoMonta,
          dataInicial: formData.dataInicio ? new Date(formData.dataInicio).toISOString() : new Date().toISOString(),
          dataCobertura: formData.dataInicio ? new Date(formData.dataInicio).toISOString() : new Date().toISOString(),
          dataDiagnostico: null,
          diagnostico: null,
          inseminador: formData.inseminador || '',
          doses: formData.dosagem || '',
          acao: 'Atualizacao' as const,
          status: 'emProcesso'
        }

        const coverageData = {
          idFazenda: formData.idFazenda,
          idVaca: formData.idVaca,
          idTouro: formData.idTouro || null,
          dataInicio: formData.dataInicio ? new Date(formData.dataInicio).toISOString() : new Date().toISOString(),
          dataFim: formData.dataFim ? new Date(formData.dataFim).toISOString() : null,
          tipoMonta: formData.tipoMonta,
          inseminador: formData.inseminador || '',
          dosagem: formData.dosagem || '',
          dataCio: formData.dataCio
            ? formData.dataCio
            : new Date(formData.dataCio).toISOString()
              ? formData.dataInicio
              : new Date(formData.dataInicio).toISOString(),
          idDiagGest: '',
          acao: 'Atualizacao' as const
        }

        await createCoupleAsync({ diagnosisData, coverageData })

        toastSyncSuccess('Salvo com sucesso!')
      }
    } catch (err: any) {
      console.error('Erro ao salvar dados:', err)
      toast.error(`Não foi possivel salvar os dados, tente novamente.`)
    }
  })

  useEffect(() => {
    if (registerCoupleData) {
      reset(registerCoupleData)
    }
  }, [registerCoupleData])

  return {
    isLoading: isLoadingCouplePlanning || isLoadingPropertyList || isLoadingLotList,
    isSaving: isLoadingCreateCouple || isLoadingCreateMultipleCouples,
    registerCoupleData,
    lotList,
    propertyList,
    validAnimalsFromLot,
    lotValidationError,
    showAnimalsModal,
    setShowAnimalsModal,
    onSubmit
  }
}
